/*
 OK FBI — Isolated Public Section Pages
 Each of these components is a REAL, dedicated route so users navigating from
 the top-nav land on a proper /route (not an in-page anchor). Layout, header,
 disclaimer, footer, and atmospheric layers all come from <PublicLayout />.

 Design rule (per OWNER directive): do NOT redesign sections here — every
 route re-uses the same cinematic section already used on the homepage.
*/
import React from "react";
import {
  PublicLayout,
  usePublicData,
  AboutSection,
  HowItWorks,
  CasePreview,
  ConnectDots,
  TheoryPreview,
  RanksAndBadges,
  AgentIdShowcase,
  Leaderboard,
  FAQSection,
} from "@/pages/PublicHomePage";
import { PrimitiveBlockList } from "@/blocks/PrimitiveBlocks";

// Small helper to push a section down so it clears the fixed public header.
function TopSpacer() {
  return <div aria-hidden style={{ height: 68 }} />;
}

export function PublicAboutPage() {
  const { pages } = usePublicData();
  const aboutSec =
    (pages?.about?.sections || []).find((s) => s.type === "about" && s.visible !== false)
    || (pages?.home?.sections || []).find((s) => s.type === "about" && s.visible !== false);
  return (
    <PublicLayout
      title="OK FBI · عن اللعبة"
      description="OK FBI — تجربة تحقيق تفاعلية خيالية سينمائية بالكامل."
    >
      <div data-testid="page-about">
        <TopSpacer />
        <AboutSection content={aboutSec} />
        <PrimitiveBlockList sections={pages?.about?.sections || []} />
      </div>
    </PublicLayout>
  );
}

export function PublicHowToPlayPage() {
  const { pages } = usePublicData();
  const sections = pages?.how_to_play?.sections || [];
  const flow    = sections.find((s) => s.type === "how_it_works"   && s.visible !== false);
  const connect = sections.find((s) => s.type === "connect_dots"   && s.visible !== false);
  const theory  = sections.find((s) => s.type === "theory_preview" && s.visible !== false);
  return (
    <PublicLayout
      title="OK FBI · طريقة اللعب"
      description="سبع مراحل تقودك من فتح الملف حتى كشف الحقيقة داخل OK FBI."
    >
      <div data-testid="page-how-to-play">
        <TopSpacer />
        {flow    ? <HowItWorks     content={flow} />    : null}
        {connect ? <ConnectDots    content={connect} /> : null}
        {theory  ? <TheoryPreview  content={theory} />  : null}
        <PrimitiveBlockList sections={sections} />
      </div>
    </PublicLayout>
  );
}

export function PublicCasePreviewPage() {
  const { pages } = usePublicData();
  const cp = (pages?.case_preview?.sections || []).find((s) => s.type === "case_preview" && s.visible !== false);
  return (
    <PublicLayout
      title="OK FBI · معاينة قضية"
      description="معاينة عامة لقضية خيالية بالكامل داخل OK FBI."
    >
      <div data-testid="page-case-preview">
        <TopSpacer />
        <CasePreview content={cp} />
        <PrimitiveBlockList sections={pages?.case_preview?.sections || []} />
      </div>
    </PublicLayout>
  );
}

export function PublicRanksPage() {
  const { ranks, pages } = usePublicData({ fetchRanks: true });
  const sections = pages?.ranks?.sections || [];
  const header = sections.find((s) => s.type === "ranks_header" && s.visible !== false);
  const badges = sections.find((s) => s.type === "badges"       && s.visible !== false);
  return (
    <PublicLayout
      title="OK FBI · الرتب والشارات"
      description="لكل محقق سجل — تعرّف على مسار الرتب والشارات داخل OK FBI."
    >
      <div data-testid="page-ranks">
        <TopSpacer />
        <RanksAndBadges ranks={ranks} header={header} badges={badges} />
        <PrimitiveBlockList sections={sections} />
      </div>
    </PublicLayout>
  );
}

export function PublicPremiumIdsPage() {
  const { pages } = usePublicData();
  const plans = pages?.plans?.sections || [];
  const header = plans.find((s) => s.type === "agent_id_header" && s.visible !== false);
  const premium = plans.find((s) => s.type === "premium_ids" && s.visible !== false);
  const memberships = plans.find((s) => s.type === "memberships" && s.visible !== false);
  return (
    <PublicLayout
      title="OK FBI · الأرقام المميزة"
      description="بطاقة المحقق والأرقام الملكية والأسطورية وVIP داخل OK FBI."
    >
      <div data-testid="page-premium-ids">
        <TopSpacer />
        <AgentIdShowcase header={header} premium={premium} memberships={memberships} />
        <PrimitiveBlockList sections={plans} />
      </div>
    </PublicLayout>
  );
}

export function PublicLeaderboardPage() {
  const { rows, pages } = usePublicData({ fetchLeaderboard: true });
  const header = (pages?.leaderboard?.sections || []).find((s) => s.type === "leaderboard_header" && s.visible !== false);
  return (
    <PublicLayout
      title="OK FBI · المتصدرون"
      description="سجل المحققين المتقدمين في منصة OK FBI — بيانات عامة فقط."
    >
      <div data-testid="page-leaderboard-public">
        <TopSpacer />
        <Leaderboard rows={rows} header={header} />
        <PrimitiveBlockList sections={pages?.leaderboard?.sections || []} />
      </div>
    </PublicLayout>
  );
}

export function PublicPlansPage() {
  const { pages } = usePublicData();
  const plans = pages?.plans?.sections || [];
  const header = plans.find((s) => s.type === "agent_id_header" && s.visible !== false);
  const premium = plans.find((s) => s.type === "premium_ids" && s.visible !== false);
  const memberships = plans.find((s) => s.type === "memberships" && s.visible !== false);
  return (
    <PublicLayout
      title="OK FBI · الباقات"
      description="مستويات الوصول داخل OK FBI — الأساسي، المميز، وستريمر."
    >
      <div data-testid="page-plans-public">
        <TopSpacer />
        <AgentIdShowcase header={header} premium={premium} memberships={memberships} />
        <PrimitiveBlockList sections={plans} />
      </div>
    </PublicLayout>
  );
}

export function PublicFaqPage() {
  const { pages } = usePublicData();
  const faq = (pages?.faq?.sections || []).find((s) => s.type === "faq" && s.visible !== false);
  return (
    <PublicLayout
      title="OK FBI · الأسئلة الشائعة"
      description="إجابات على الأسئلة المتكررة حول OK FBI."
    >
      <div data-testid="page-faq">
        <TopSpacer />
        <FAQSection content={faq} />
        <PrimitiveBlockList sections={pages?.faq?.sections || []} />
      </div>
    </PublicLayout>
  );
}
